import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { Circles } from "react-loader-spinner";
import { toast } from "react-toastify";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { ModalOverlay } from "../../../components/ModalOverlay";
import baseUrl from "../../../api/baseUrl";
import socket from "../../../api/socket";
import getDecryptedToken from "../../../api/getDecryptedToken";
import ModalHeader from "./ModalHeader";
import type { PatientTokenInfo } from "./ModalHeader";
import ProfileHeader from "./ProfileHeader";
import MedicalTab from "./MedicalTab";
import ExaminationTab from "./ExaminationTab";
import InvoicesTab from "./InvoicesTab";
import {
  setPrintaction,
  setPrintAmount,
  setPrintclinic,
  setPrintPatient,
  setPrintSubTreatment,
  setPrintTreatment,
  setPrintTemplate2action,
  setPrintTemplate2Amount,
  setPrintTemplate2clinic,
  setPrintTemplate2Patient,
  setPrintTemplate2SubTreatment,
  setPrintTemplate2Treatment,
} from "../../../redux/printstate/printActions";
import { useInvoicePrintTemplate } from "../../../context/InvoicePrintTemplateContext";
import Printtest from "../../setting/manageprint/printtest/Printtest";
import NewBillPrintRuntime from "../../setting/manageprint/newBillPrint/NewBillPrintRuntime";
import {
  printNewBillFromElement,
} from "../../setting/manageprint/newBillPrint/newBillStorage";
import {
  SummaryBadge,
  SummarySection,
  SUMMARY_SPACING,
  summarySectionId,
} from "./patientSummaryUi";
import type { SummaryTheme } from "./patientSummaryUi";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import AddIcon from "@mui/icons-material/Add";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import {
  isDoctorTransferEnabled,
  isCertificatePrintEnabled,
  isTreatmentsEnabled,
  isBillingEnabled,
} from "../../../utils/clinicFeatures";
import FullPatientEditModal from "./FullPatientEditModal";
import PatientCertificatePrintModal from "./PatientCertificatePrintModal";
import SendLabOrderModal from "../../../components/labs/SendLabOrderModal";
import { useLabCatalog, useLabPriceList } from "../../../components/labs/useLabCatalog";
import { useClinicDentists } from "../../../components/labs/useClinicDentists";
import {
  buildClinicOrderPayload,
  clinicSenderDefaults,
  emptyLabOrderValues,
  type LabOrderFormValues,
  orderLinesError,
} from "../../../components/labs/labOrderFormUtils";
import type { ClinicDetails } from "../../../components/labs/LabOrderForm";
import {
  draftPhotoFailureMessage,
  uploadDraftLabOrderPhotos,
  type DraftLabOrderPhoto,
} from "../../../components/labs/labOrderPhotoUtils";
import {
  formatPatientLabel,
  formatPatientMeta,
} from "../../../components/labs/labPatientUtils";

type TransferAppointmentStatus = "waiting" | "completed";

type LabPartner = {
  id: string;
  labId: string | null;
  name: string;
  address?: string;
  phone?: string;
  canSendOrders?: boolean;
  isOwnLab?: boolean;
  status?: string;
};

interface PatientViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onActionClose?: () => void;
  patientData: any;
  loading: boolean;
  theme?: SummaryTheme;
  onEdit?: (id: string, e: React.MouseEvent) => void;
  onAddTreatment?: (id: string, e: React.MouseEvent) => void;
  onAddDiagnosis?: (id: string, e: React.MouseEvent) => void;
  onCreateInvoice?: (id: string, e: React.MouseEvent) => void;
  onSendLab?: (id: string, e: React.MouseEvent) => void;
  onDelete?: (id: string, e: React.MouseEvent) => void;
  canDelete?: boolean;
  overlayClassName?: string;
  /** When set (from an appointment row), shows transfer-to-doctor controls. */
  appointmentId?: string | null;
  transferDoctorNames?: string;
  onTransferSuccess?: () => void;
  onPatientUpdated?: (patient: any) => void;
  tokenInfo?: PatientTokenInfo | null;
}

const NAV_ITEMS = [
  { key: "medical", label: "Medical", icon: MonitorHeartOutlinedIcon },
  { key: "treatments", label: "Treatments", icon: MedicalServicesOutlinedIcon },
  { key: "billing", label: "Billing", icon: ReceiptLongOutlinedIcon },
] as const;

const isLongTermTreatment = (item: any) => item?.status === "long term";

const isPricedSubTreatment = (subItem: any) =>
  subItem?.selected_option === "treatment_paid" ||
  subItem?.selected_option === "treatment_stage";

/** Long-term estimate (full price) and remaining due after billed stages/subs. */
const getLongTermPrintSummary = (treatments: any[]) =>
  (treatments ?? []).reduce(
    (acc, item) => {
      if (!isLongTermTreatment(item)) return acc;
      const estimate = parseFloat(item?.price) || 0;
      const billed = (item?.subTreatment ?? []).reduce((subSum: number, sub: any) => {
        if (!isPricedSubTreatment(sub)) return subSum;
        return subSum + (parseFloat(sub?.price) || 0);
      }, 0);
      return {
        estimation: acc.estimation + estimate,
        longTermDue: acc.longTermDue + Math.max(0, estimate - billed),
      };
    },
    { estimation: 0, longTermDue: 0 }
  );

function dentistOptionLabel(dentist: {
  title?: string;
  firstName?: string;
  lastName?: string;
}): string {
  const name = [dentist.firstName, dentist.lastName].filter(Boolean).join(" ").trim();
  if (name) return name;
  return [dentist.title, dentist.lastName].filter(Boolean).join(" ").trim() || "Doctor";
}

const PatientViewModal: React.FC<PatientViewModalProps> = ({
  isOpen,
  onClose,
  onActionClose,
  patientData,
  loading,
  theme = "light",
  onAddTreatment,
  onAddDiagnosis,
  onCreateInvoice,
  onSendLab,
  onDelete,
  canDelete = false,
  overlayClassName = "fixed inset-0 z-50 flex h-dvh w-full flex-col overflow-y-auto overscroll-contain",
  appointmentId,
  transferDoctorNames,
  onTransferSuccess,
  onPatientUpdated,
  tokenInfo,
}) => {
  const handleActionClose = onActionClose ?? onClose;
  const dispatch = useDispatch();
  const { template: printTemplate } = useInvoicePrintTemplate();
  const doctorTransferEnabled = isDoctorTransferEnabled();
  const certificatePrintEnabled = isCertificatePrintEnabled();
  const showTransfer = doctorTransferEnabled && Boolean(appointmentId);
  const labsFeatureEnabled = getDecryptedToken("featureLabs") === "true";
  // BookMyDentist only clinics see the Medical section alone
  const treatmentsEnabled = isTreatmentsEnabled();
  const billingEnabled = isBillingEnabled();
  const navItems = NAV_ITEMS.filter(
    ({ key }) =>
      (key !== "treatments" || treatmentsEnabled) && (key !== "billing" || billingEnabled)
  );
  const clinicDetails: ClinicDetails = useMemo(
    () => ({
      name: getDecryptedToken("clinicName") || "Clinic",
      phone: getDecryptedToken("clinicPhoneNumber") || undefined,
      address: getDecryptedToken("address") || undefined,
    }),
    []
  );

  const [examinationData, setExaminationData] = useState<any[]>([]);
  const [invoicesData, setInvoicesData] = useState<any[]>([]);
  const [loadingExamination, setLoadingExamination] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [dentists, setDentists] = useState<
    Array<{ _id: string; title?: string; firstName?: string; lastName?: string }>
  >([]);
  const [transferDoctorId, setTransferDoctorId] = useState("");
  const [transferSaving, setTransferSaving] = useState(false);
  const [transferConfirmOpen, setTransferConfirmOpen] = useState(false);
  const [transferStatus, setTransferStatus] =
    useState<TransferAppointmentStatus>("waiting");
  const [editOpen, setEditOpen] = useState(false);
  const [certificatePrintOpen, setCertificatePrintOpen] = useState(false);
  const [localPatient, setLocalPatient] = useState(patientData);

  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [partners, setPartners] = useState<LabPartner[]>([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [labOrderValues, setLabOrderValues] = useState<LabOrderFormValues>(() => ({
    ...emptyLabOrderValues(),
    ...clinicSenderDefaults(clinicDetails),
  }));
  const [labSubmitting, setLabSubmitting] = useState(false);
  const [labError, setLabError] = useState("");
  const [labDraftPhotos, setLabDraftPhotos] = useState<DraftLabOrderPhoto[]>([]);
  const [labPhotoUploadProgress, setLabPhotoUploadProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [patientError, setPatientError] = useState(false);
  const [treatmentDateSort, setTreatmentDateSort] = useState<"desc" | "asc">("asc");
  const [treatmentStatusSort, setTreatmentStatusSort] = useState<
    "planned" | "ongoing" | "Complete" | "long term" | null
  >(null);

  const printRef = useRef<HTMLDivElement>(null);
  const [printData, setPrintData] = useState<any>(null);
  const [printingPaymentId, setPrintingPaymentId] = useState<string | null>(null);

  useEffect(() => {
    setLocalPatient(patientData);
  }, [patientData]);

  const displayPatient = localPatient ?? patientData;

  const patientLabel = useMemo(
    () => (displayPatient ? formatPatientLabel(displayPatient) : ""),
    [displayPatient]
  );
  const patientMeta = useMemo(
    () => (displayPatient ? formatPatientMeta(displayPatient) : ""),
    [displayPatient]
  );

  const handleEditDetails = useCallback((_id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditOpen(true);
  }, []);

  const handlePatientSaved = useCallback(async () => {
    const id = displayPatient?._id || patientData?._id;
    if (!id) return;
    try {
      const res = await baseUrl.get("patient/findById", {
        headers: { patientid: id },
      });
      const next = res.data?.data ?? null;
      setLocalPatient(next);
      onPatientUpdated?.(next);
    } catch (err) {
      console.error(err);
    }
  }, [displayPatient?._id, patientData?._id, onPatientUpdated]);

  useEffect(() => {
    if (!labsFeatureEnabled || !isOpen) {
      setPartnersLoading(false);
      return;
    }
    setPartnersLoading(true);
    baseUrl
      .get("client/labs/partners")
      .then((res) => {
        const list = (res.data.data ?? []).filter(
          (p: LabPartner) =>
            p.labId &&
            (p.canSendOrders === true ||
              (p.canSendOrders !== false && p.status === "active"))
        );
        setPartners(list);
        const pick = list[0];
        if (pick?.labId) {
          setLabOrderValues((prev) => ({
            ...prev,
            labId: String(pick.labId),
            labPartnerId: pick.id,
          }));
        }
      })
      .catch(() => setPartners([]))
      .finally(() => setPartnersLoading(false));
  }, [labsFeatureEnabled, isOpen]);

  const { items: priceListItems, loading: priceListLoading } = useLabPriceList(
    labOrderValues.labId,
    labOrderValues.labPartnerId,
    labsFeatureEnabled && sendModalOpen
  );
  const { catalog } = useLabCatalog(labsFeatureEnabled && sendModalOpen);
  const labDentists = useClinicDentists(labsFeatureEnabled && sendModalOpen);

  useEffect(() => {
    if (labOrderValues.patient.trim()) setPatientError(false);
  }, [labOrderValues.patient]);

  const openSendModal = useCallback(() => {
    if (!displayPatient) return;
    const regNo = displayPatient.regNo?.trim() || "";
    setLabError("");
    setPatientError(false);
    setLabOrderValues((prev) => ({
      ...emptyLabOrderValues(),
      ...clinicSenderDefaults(clinicDetails),
      labId: prev.labId,
      labPartnerId: prev.labPartnerId,
      patient: patientLabel,
      patientMeta,
      caseTitle: regNo ? `Case — ${regNo}` : "",
    }));
    setSendModalOpen(true);
  }, [clinicDetails, displayPatient, patientLabel, patientMeta]);

  const closeSendModal = useCallback(() => {
    if (labSubmitting) return;
    setSendModalOpen(false);
    setLabError("");
    setLabDraftPhotos([]);
  }, [labSubmitting]);

  const handleSendLab = useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      if (onSendLab) {
        onSendLab(id, e);
        return;
      }
      openSendModal();
    },
    [onSendLab, openSendModal]
  );

  const submitLabOrder = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!labOrderValues.patient.trim()) {
        setPatientError(true);
        setLabError("Select or add a patient");
        return;
      }
      setPatientError(false);
      if (
        !labOrderValues.labId ||
        !labOrderValues.labPartnerId ||
        !labOrderValues.caseTitle.trim()
      ) {
        setLabError("Select a lab and enter case details");
        return;
      }
      if (!labOrderValues.toothNos.length) {
        setLabError("Select at least one tooth");
        return;
      }
      if (!labOrderValues.dueDate) {
        setLabError("Due date is required");
        return;
      }
      const linesError = orderLinesError(labOrderValues.invoiceItems);
      if (linesError) {
        setLabError(linesError);
        return;
      }
      setLabSubmitting(true);
      setLabError("");
      baseUrl
        .post("client/labs/orders", buildClinicOrderPayload(labOrderValues))
        .then(async (res) => {
          const msg =
            res.data?.data?.message ||
            res.data?.message ||
            "Order sent to lab";
          toast.success(msg);
          // The order exists now — photos go up one by one; failures don't undo it.
          const mongoId = res.data?.data?._id;
          const failed = await uploadDraftLabOrderPhotos(
            mongoId ? String(mongoId) : null,
            labDraftPhotos,
            (current, total) => setLabPhotoUploadProgress({ current, total })
          );
          setLabPhotoUploadProgress(null);
          if (failed > 0) toast.warn(draftPhotoFailureMessage(failed));
          setLabDraftPhotos([]);
          setSendModalOpen(false);
          setLabOrderValues((prev) => ({
            ...emptyLabOrderValues(),
            ...clinicSenderDefaults(clinicDetails),
            labId: prev.labId,
            labPartnerId: prev.labPartnerId,
            patient: patientLabel,
            patientMeta,
          }));
        })
        .catch((err) => {
          const msg =
            err?.response?.data?.message || "Failed to send lab order";
          setLabError(msg);
          toast.error(msg);
        })
        .finally(() => setLabSubmitting(false));
    },
    [clinicDetails, labDraftPhotos, labOrderValues, patientLabel, patientMeta]
  );

  const partnerOptions = useMemo(
    () =>
      partners
        .filter((p): p is LabPartner & { labId: string } => Boolean(p.labId))
        .map((p) => ({
          id: p.id,
          labId: String(p.labId),
          name: p.name,
          address: p.address,
          phone: p.phone,
          isOwnLab: p.isOwnLab,
        })),
    [partners]
  );

  const showSendLab = labsFeatureEnabled;
  useEffect(() => {
    if (isOpen && displayPatient?._id) {
      setLoadingExamination(true);
      baseUrl
        .get("/patient/findTreatmentByPid", { headers: { patientid: displayPatient._id } })
        .then((res: any) => setExaminationData(res.data.data || []))
        .catch(() => setExaminationData([]))
        .finally(() => setLoadingExamination(false));

      setLoadingInvoices(true);
      baseUrl
        .get("/payment/getPaymentByPatient", { headers: { patientid: displayPatient._id } })
        .then((res: any) => setInvoicesData(res.data.data || []))
        .catch(() => setInvoicesData([]))
        .finally(() => setLoadingInvoices(false));
    }
  }, [isOpen, displayPatient?._id]);

  useEffect(() => {
    if (!isOpen || !showTransfer) return;
    setTransferDoctorId("");
    setTransferConfirmOpen(false);
    setTransferStatus("waiting");
    baseUrl
      .get("/appointment/findAllAppointmentDentist")
      .then((response) => {
        setDentists(Array.isArray(response.data?.data) ? response.data.data : []);
      })
      .catch((err) => console.error(err));
  }, [isOpen, showTransfer, appointmentId]);

  const openTransferConfirm = useCallback(() => {
    if (!appointmentId || !transferDoctorId) return;
    setTransferStatus("waiting");
    setTransferConfirmOpen(true);
  }, [appointmentId, transferDoctorId]);

  const handleTransferPatient = useCallback(() => {
    if (!appointmentId || !transferDoctorId || !transferStatus) return;
    setTransferSaving(true);
    baseUrl
      .patch(
        "/appointment/transferPatientDoctor",
        { transferDoctorId, status: transferStatus },
        { headers: { appointmentid: appointmentId } }
      )
      .then(() => {
        socket.emit("allAppointmentStatus", {
          appointmentid: appointmentId,
          status: transferStatus,
        });
        setTransferDoctorId("");
        setTransferConfirmOpen(false);
        toast.success(
          transferStatus === "completed"
            ? "Patient transferred and marked completed"
            : "Patient transferred and put on hold"
        );
        onTransferSuccess?.();
      })
      .catch((err) => {
        console.error(err);
        toast.error(
          err?.response?.data?.error || "Could not transfer patient"
        );
      })
      .finally(() => setTransferSaving(false));
  }, [
    appointmentId,
    onTransferSuccess,
    transferDoctorId,
    transferStatus,
  ]);

  const treatmentCount = useMemo(
    () =>
      examinationData.reduce((n, item) => {
        const treatments = Array.isArray(item?.treatments) ? item.treatments : [];
        return (
          n +
          treatments.reduce(
            (sum: number, treatment: any) =>
              sum + 1 + (Array.isArray(treatment?.subTreatment) ? treatment.subTreatment.length : 0),
            0
          )
        );
      }, 0),
    [examinationData]
  );

  const billingSummary = useMemo(() => {
    const grouped = new Map<string, any[]>();

    invoicesData.forEach((item) => {
      const treatmentId = String(item?.treatmentId ?? "");
      if (!treatmentId) return;
      const existing = grouped.get(treatmentId) ?? [];
      existing.push(item);
      grouped.set(treatmentId, existing);
    });

    let totalBill = 0;
    let totalPaid = 0;
    let netDue = 0;

    if (grouped.size > 0) {
      grouped.forEach((payments) => {
        const total = payments.reduce((sum, payment) => sum + Number(payment?.total || 0), 0);
        const paid = payments.reduce((sum, payment) => sum + Number(payment?.paid || 0), 0);
        const discount = payments.reduce(
          (sum, payment) => sum + Number(payment?.discount || 0),
          0
        );
        totalBill += total;
        totalPaid += paid;
        // Per treatment: remaining = billed − paid − discount (not Σ receipt dues)
        netDue += total - paid - discount;
      });
    } else {
      totalBill = invoicesData.reduce((sum, item) => sum + Number(item?.total || 0), 0);
      totalPaid = invoicesData.reduce((sum, item) => sum + Number(item?.paid || 0), 0);
      const discount = invoicesData.reduce(
        (sum, item) => sum + Number(item?.discount || 0),
        0
      );
      netDue = totalBill - totalPaid - discount;
    }

    return { totalBill, totalPaid, netDue };
  }, [invoicesData]);

  const dispatchPrintActions = useCallback(
    (e: any, treatmentsForSummary: any[]) => {
      if (printTemplate === "newBillPrint") return;

      const isTemplate2 = printTemplate === "printTemplate_2";
      const setAction = isTemplate2 ? setPrintTemplate2action : setPrintaction;
      const setClinic = isTemplate2 ? setPrintTemplate2clinic : setPrintclinic;
      const setPatient = isTemplate2 ? setPrintTemplate2Patient : setPrintPatient;
      const setAmount = isTemplate2 ? setPrintTemplate2Amount : setPrintAmount;
      const setTreatmentPrint = isTemplate2 ? setPrintTemplate2Treatment : setPrintTreatment;
      const setSubTreatment = isTemplate2
        ? setPrintTemplate2SubTreatment
        : setPrintSubTreatment;

      const layoutFields = isTemplate2
        ? e?.amounts?.clinicId?.printTemplate_2
        : e?.amounts?.clinicId?.bills;

      layoutFields?.forEach((element: any) => {
        const { title, top, left, show, size, height, width } = element;
        if (top) dispatch(setAction({ name: title, target: "top", value: top }));
        if (left) dispatch(setAction({ name: title, target: "left", value: left }));
        if (show !== undefined)
          dispatch(setAction({ name: title, target: "show", value: show }));
        if (size) dispatch(setAction({ name: title, target: "size", value: size }));
        if (height)
          dispatch(setAction({ name: title, target: "height", value: height }));
        if (width) dispatch(setAction({ name: title, target: "width", value: width }));
      });

      const clinicPayload = {
        clinicName: e?.amounts?.clinicId?.title,
        tpNo: e?.amounts?.clinicId?.tpNo,
        address: e?.amounts?.clinicId?.address,
        drName:
          "Dr " +
          e?.amounts?.doctorId?.firstName +
          " " +
          e?.amounts?.doctorId?.lastName,
        printImages: e?.amounts?.clinicId?.printImages,
        headerImages: e?.amounts?.clinicId?.headerImages,
        footerImages: e?.amounts?.clinicId?.footerImages,
      };
      dispatch(setClinic(clinicPayload));

      dispatch(
        setPatient({
          invoiceNo: e?.amounts?.invoiceNo,
          invoiceDate: e?.amounts?.updatedAt,
          regNo: e?.patientData?.regNo,
          fullName: e?.patientData?.fullName,
          mobileNo: e?.patientData?.mobileNo,
          gender: e?.patientData?.gender,
          dob: e?.patientData?.dob,
        })
      );
      const { estimation, longTermDue } = getLongTermPrintSummary(treatmentsForSummary);

      dispatch(
        setAmount({
          total: e?.amounts?.total,
          lastDue: e?.amounts?.lastDue,
          discount: e?.amounts?.discount,
          netotal: e?.amounts?.netotal,
          card: e?.amounts?.card,
          cash: e?.amounts?.cash,
          bankTransfer: e?.amounts?.bankTransfer,
          online: e?.amounts?.online,
          longTermDue,
          estimation,
        })
      );
      dispatch(
        setTreatmentPrint(
          e?.treatmentdata?.filter((item: any) => item?.status !== "long term") ?? []
        )
      );
      dispatch(
        setSubTreatment(
          e?.subPayments?.filter((sub: any) => sub?.status !== "long term") ?? []
        )
      );
    },
    [dispatch, printTemplate]
  );

  const handlePrinting = useCallback(async () => {
    if (!printRef.current) {
      setPrintingPaymentId(null);
      return;
    }
    printRef.current.style.display = "block";
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });

    if (printTemplate === "newBillPrint") {
      try {
        await printNewBillFromElement(
          printRef.current,
          printData?.amounts?.clinicId?.newBillPrint
        );
      } catch (error) {
        console.error(error);
        toast.error("Could not print New Bill layout");
      } finally {
        if (printRef.current) printRef.current.style.display = "none";
        setPrintingPaymentId(null);
      }
      return;
    }

    const element = printRef.current;
    const options = {
      scale: 2,
      useCORS: true,
      allowTaint: true,
    };

    const canvas = await html2canvas(element, options);
    const imgData = canvas.toDataURL("image/png");

    const imgWidth = 210;
    const imgProps = canvas.width / canvas.height;
    const imgHeight = imgWidth / imgProps;

    const pdf = new jsPDF({
      orientation: "p",
      unit: "mm",
      format: [imgWidth, imgHeight],
    });

    pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight, "", "FAST");

    const pdfBlob = pdf.output("blob");
    const blobUrl = URL.createObjectURL(pdfBlob);

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.top = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    printRef.current.style.transform = "none";
    document.body.appendChild(iframe);
    iframe.src = blobUrl;

    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        if (printRef.current) printRef.current.style.display = "none";
        setPrintingPaymentId(null);
      }, 500);
    };
  }, [printTemplate, printData]);

  useEffect(() => {
    if (printData) {
      handlePrinting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printData]);

  const handlePrintInvoice = useCallback(
    async (paymentId: string, treatmentId: string) => {
      if (!paymentId || printingPaymentId) return;
      setPrintingPaymentId(paymentId);
      try {
        const response = await baseUrl.get("payment/getPaymentById", {
          headers: { paymentid: paymentId },
        });
        const treatmentsForSummary =
          examinationData.find((item) => String(item?._id) === String(treatmentId))
            ?.treatments ?? [];
        dispatchPrintActions(response.data, treatmentsForSummary);
        setPrintData(response.data);
      } catch (error) {
        console.error(error);
        toast.error("Failed to load invoice for printing");
        setPrintingPaymentId(null);
      }
    },
    [printingPaymentId, examinationData, dispatchPrintActions]
  );

  const scrollTo = (key: string) => {
    document.getElementById(summarySectionId(key))?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!isOpen) return null;

  const isDark = theme === "dark";

  return (
    <ModalOverlay
      className={`${overlayClassName} ${isDark ? "bg-slate-900" : "bg-slate-50"}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="patient-summary-title"
    >
      <div className={`flex min-h-full flex-col ${isDark ? "bg-slate-900" : "bg-slate-50"}`}>
        {loading || !displayPatient ? (
          <ModalHeader onClose={onClose} theme={theme} />
        ) : (
          <ProfileHeader
            patientData={displayPatient}
            theme={theme}
            onEdit={handleEditDetails}
            onDelete={onDelete}
            onSendLab={showSendLab ? handleSendLab : undefined}
            onPrintCertificate={
              certificatePrintEnabled ? () => setCertificatePrintOpen(true) : undefined
            }
            canDelete={canDelete}
            onClose={onClose}
            onActionClose={handleActionClose}
            tokenInfo={tokenInfo}
            examinationData={examinationData}
            aside={
              showTransfer ? (
                <div className="flex flex-col gap-2.5">
                  <div
                    className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wide ${
                      isDark ? "text-slate-400" : "text-slate-500"
                    }`}
                  >
                    <SwapHorizIcon sx={{ fontSize: 16 }} />
                    Transfer to doctor
                  </div>
                  {transferDoctorNames ? (
                    <p
                      className={`text-xs ${
                        isDark ? "text-sky-300" : "text-sky-700"
                      }`}
                    >
                      Already transferred to {transferDoctorNames}
                    </p>
                  ) : null}
                  <p
                    className={`text-xs leading-snug ${
                      isDark ? "text-slate-400" : "text-slate-500"
                    }`}
                  >
                    Keeps the original doctor and adds the selected doctor to
                    the transfer list.
                  </p>
                  <div className="flex gap-2">
                    <select
                      value={transferDoctorId}
                      onChange={(e) => setTransferDoctorId(e.target.value)}
                      disabled={transferSaving || !appointmentId}
                      className={`min-w-0 flex-1 rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/25 ${
                        isDark
                          ? "border-slate-600 bg-slate-900 text-slate-100"
                          : "border-slate-200 bg-white text-slate-800"
                      }`}
                    >
                      <option value="">Select doctor</option>
                      {dentists.map((dentist) => (
                        <option key={dentist._id} value={dentist._id}>
                          {dentistOptionLabel(dentist)}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={openTransferConfirm}
                      disabled={
                        transferSaving || !transferDoctorId || !appointmentId
                      }
                      className="shrink-0 rounded-lg bg-sky-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-sky-700 disabled:opacity-40"
                    >
                      Transfer
                    </button>
                  </div>
                </div>
              ) : undefined
            }
          />
        )}

        <div className="flex-1">
          <div className={`mx-auto max-w-[1400px] ${SUMMARY_SPACING.pageX} ${SUMMARY_SPACING.pageY}`}>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Circles height="44" width="44" color="#6366f1" ariaLabel="loading" />
              </div>
            ) : displayPatient ? (
              <div className={`flex flex-col ${SUMMARY_SPACING.stack}`}>
                <nav
                  aria-label="Summary sections"
                  className={`sticky top-0 z-10 -mx-4 flex gap-1 overflow-x-auto px-4 py-1.5 sm:-mx-6 sm:gap-1.5 sm:px-6 ${
                    isDark ? "bg-slate-900/95 backdrop-blur-sm" : "bg-slate-50/95 backdrop-blur-sm"
                  }`}
                >
                  {navItems.map(({ key, label, icon: Icon }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => scrollTo(key)}
                      className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors sm:px-3 sm:py-2 ${
                        isDark
                          ? "text-slate-300 hover:bg-slate-800 hover:text-white"
                          : "text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm"
                      }`}
                    >
                      <Icon sx={{ fontSize: 15 }} />
                      {label}
                    </button>
                  ))}
                </nav>

                <SummarySection
                  id={summarySectionId("medical")}
                  title="Medical Alerts"
                  icon={<MonitorHeartOutlinedIcon sx={{ fontSize: 16 }} />}
                  theme={theme}
                  className={SUMMARY_SPACING.scrollMt}
                >
                  <MedicalTab patientData={displayPatient} theme={theme} />
                </SummarySection>

                {treatmentsEnabled && (
                <SummarySection
                  id={summarySectionId("treatments")}
                  title="Treatment History"
                  icon={<MedicalServicesOutlinedIcon sx={{ fontSize: 16 }} />}
                  titleExtra={
                    <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTreatmentDateSort((prev) =>
                            prev === "desc" ? "asc" : "desc"
                          );
                        }}
                        title={
                          treatmentDateSort === "desc"
                            ? "Sorted newest first — click for oldest first"
                            : "Sorted oldest first — click for newest first"
                        }
                        aria-label={
                          treatmentDateSort === "desc"
                            ? "Sort by date: newest first"
                            : "Sort by date: oldest first"
                        }
                        className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-semibold transition-colors ${
                          isDark
                            ? "border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700"
                            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 shadow-sm"
                        }`}
                      >
                        {treatmentDateSort === "desc" ? (
                          <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                        ) : (
                          <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                        )}
                        {treatmentDateSort === "desc" ? "Newest" : "Oldest"}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTreatmentStatusSort((prev) =>
                            prev === null
                              ? "planned"
                              : prev === "planned"
                                ? "ongoing"
                                : prev === "ongoing"
                                  ? "Complete"
                                  : prev === "Complete"
                                    ? "long term"
                                    : null
                          );
                        }}
                        title={
                          treatmentStatusSort === null
                            ? "Sort by status — click for planned first"
                            : treatmentStatusSort === "planned"
                              ? "Planned first — click for ongoing first"
                              : treatmentStatusSort === "ongoing"
                                ? "Ongoing first — click for complete first"
                                : treatmentStatusSort === "Complete"
                                  ? "Complete first — click for treatment plan first"
                                  : "Treatment plan first — click to clear status sort"
                        }
                        aria-label={
                          treatmentStatusSort === null
                            ? "Sort by status"
                            : treatmentStatusSort === "long term"
                              ? "Sort by status: treatment plan first"
                              : `Sort by status: ${treatmentStatusSort} first`
                        }
                        className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-semibold transition-colors ${
                          treatmentStatusSort
                            ? isDark
                              ? "border-amber-500/50 bg-amber-500/15 text-amber-200"
                              : "border-amber-300 bg-amber-50 text-amber-800 shadow-sm"
                            : isDark
                              ? "border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700"
                              : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 shadow-sm"
                        }`}
                      >
                        {treatmentStatusSort === null
                          ? "Status"
                          : treatmentStatusSort === "planned"
                            ? "Planned"
                            : treatmentStatusSort === "ongoing"
                              ? "Ongoing"
                              : treatmentStatusSort === "Complete"
                                ? "Complete"
                                : "Treatment Plan"}
                      </button>
                    </div>
                  }
                  badge={
                    <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                      {treatmentCount > 0 ? (
                        <SummaryBadge tone="info" theme={theme}>
                          {treatmentCount}
                        </SummaryBadge>
                      ) : loadingExamination ? (
                        <SummaryBadge theme={theme}>…</SummaryBadge>
                      ) : null}
                      {(onAddTreatment || onAddDiagnosis) && (
                        <>
                          {onAddTreatment && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (displayPatient?._id) {
                                  onAddTreatment(displayPatient._id, e);
                                  handleActionClose();
                                }
                              }}
                              className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors ${
                                isDark
                                  ? "bg-emerald-600 text-white hover:bg-emerald-500"
                                  : "bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm"
                              }`}
                            >
                              <MedicalInformationIcon sx={{ fontSize: 14 }} />
                              Add Treatment
                            </button>
                          )}
                          {onAddDiagnosis && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (displayPatient?._id) {
                                  onAddDiagnosis(displayPatient._id, e);
                                  handleActionClose();
                                }
                              }}
                              className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors ${
                                isDark
                                  ? "bg-teal-600 text-white hover:bg-teal-500"
                                  : "bg-teal-600 text-white hover:bg-teal-700 shadow-sm"
                              }`}
                            >
                              <AddIcon sx={{ fontSize: 14 }} />
                              Add Diagnosis
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  }
                  theme={theme}
                  className={SUMMARY_SPACING.scrollMt}
                >
                  <ExaminationTab
                    examinationData={examinationData}
                    loading={loadingExamination}
                    theme={theme}
                    dateSort={treatmentDateSort}
                    statusSort={treatmentStatusSort}
                    appointmentId={appointmentId}
                  />
                </SummarySection>
                )}

                {billingEnabled && (
                <SummarySection
                  id={summarySectionId("billing")}
                  title="Billing & Invoices"
                  icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 16 }} />}
                  badge={
                    onCreateInvoice ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (displayPatient?._id) {
                            onCreateInvoice(displayPatient._id, e);
                            handleActionClose();
                          }
                        }}
                        className={`inline-flex shrink-0 items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors ${
                          isDark
                            ? "bg-indigo-600 text-white hover:bg-indigo-500"
                            : "bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm"
                        }`}
                      >
                        <AddIcon sx={{ fontSize: 14 }} />
                        Create Invoice
                      </button>
                    ) : undefined
                  }
                  theme={theme}
                  className={SUMMARY_SPACING.scrollMt}
                >
                  <InvoicesTab
                    invoicesData={invoicesData}
                    loading={loadingInvoices}
                    theme={theme}
                    hidePatientColumn
                    onPrintInvoice={handlePrintInvoice}
                    printingPaymentId={printingPaymentId}
                  />
                  {loadingInvoices ? (
                    <div className="mt-3 flex justify-end">
                      <SummaryBadge theme={theme}>…</SummaryBadge>
                    </div>
                  ) : invoicesData.length > 0 ? (
                    <div
                      className={`mt-3 flex flex-wrap items-center justify-end gap-2 border-t pt-3 ${
                        isDark ? "border-slate-700/80" : "border-slate-100"
                      }`}
                    >
                      <SummaryBadge tone="neutral" theme={theme}>
                        Total Rs {billingSummary.totalBill.toLocaleString()}
                      </SummaryBadge>
                      <SummaryBadge tone="info" theme={theme}>
                        Paid Rs {billingSummary.totalPaid.toLocaleString()}
                      </SummaryBadge>
                      {billingSummary.netDue > 0 ? (
                        <SummaryBadge tone="danger" theme={theme}>
                          Due Rs {billingSummary.netDue.toLocaleString()}
                        </SummaryBadge>
                      ) : billingSummary.netDue < 0 ? (
                        <SummaryBadge tone="success" theme={theme}>
                          Advance Rs {Math.abs(billingSummary.netDue).toLocaleString()}
                        </SummaryBadge>
                      ) : (
                        <SummaryBadge tone="success" theme={theme}>
                          Due Rs 0
                        </SummaryBadge>
                      )}
                    </div>
                  ) : null}
                </SummarySection>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-16">
                <p className={isDark ? "text-slate-400" : "text-slate-500"}>No patient data available.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <FullPatientEditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        patientId={displayPatient?._id}
        theme={theme}
        onSaved={handlePatientSaved}
      />
      {certificatePrintEnabled ? (
        <PatientCertificatePrintModal
          open={certificatePrintOpen}
          onClose={() => setCertificatePrintOpen(false)}
          patient={displayPatient}
          treatments={examinationData}
          theme={theme}
        />
      ) : null}
      {labsFeatureEnabled ? (
        <SendLabOrderModal
          open={sendModalOpen}
          onClose={closeSendModal}
          partners={partnerOptions}
          values={labOrderValues}
          onChange={setLabOrderValues}
          onSubmit={submitLabOrder}
          submitting={labSubmitting}
          error={labError}
          clinicDetails={clinicDetails}
          priceListItems={priceListItems}
          priceListLoading={priceListLoading}
          catalog={catalog}
          dentists={labDentists}
          patientError={patientError}
          photos={labDraftPhotos}
          onPhotosChange={setLabDraftPhotos}
          photoUploadProgress={labPhotoUploadProgress}
        />
      ) : null}
      {transferConfirmOpen
        ? createPortal(
            <div
              className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-slate-900/50"
              role="dialog"
              aria-modal="true"
              aria-labelledby="transfer-status-title"
              onClick={() => {
                if (!transferSaving) setTransferConfirmOpen(false);
              }}
            >
              <div
                className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 shadow-xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
                  <div>
                    <h2
                      id="transfer-status-title"
                      className="text-base font-semibold text-slate-900 dark:text-slate-100"
                    >
                      Transfer patient
                    </h2>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                      Mark this appointment as on hold or completed before
                      transferring to the selected doctor.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setTransferConfirmOpen(false)}
                    disabled={transferSaving}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
                    aria-label="Close"
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </button>
                </div>

                <div className="px-5 pb-2">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={transferSaving}
                      onClick={() => setTransferStatus("waiting")}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        transferStatus === "waiting"
                          ? "bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 border-violet-300 dark:border-violet-600"
                          : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600"
                      }`}
                    >
                      On hold
                    </button>
                    <button
                      type="button"
                      disabled={transferSaving}
                      onClick={() => setTransferStatus("completed")}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        transferStatus === "completed"
                          ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-600"
                          : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600"
                      }`}
                    >
                      Completed
                    </button>
                  </div>
                </div>

                <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-100 dark:border-slate-700">
                  <button
                    type="button"
                    onClick={() => setTransferConfirmOpen(false)}
                    disabled={transferSaving}
                    className="px-3.5 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-600 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={transferSaving || !transferStatus}
                    onClick={handleTransferPatient}
                    className="px-3.5 py-2 rounded-lg text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 disabled:opacity-50"
                  >
                    {transferSaving ? "Transferring…" : "Confirm transfer"}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
      <div style={{ position: "absolute", left: "-9999px" }}>
        <div ref={printRef} style={{ display: "none" }}>
          {printTemplate === "newBillPrint" && printData ? (
            <NewBillPrintRuntime
              storeRaw={printData?.amounts?.clinicId?.newBillPrint}
              paymentResponse={printData}
            />
          ) : printTemplate === "printTemplate_2" ? (
            <Printtest stateKey="printTemplate2" />
          ) : (
            <Printtest />
          )}
        </div>
      </div>
    </ModalOverlay>
  );
};

export default PatientViewModal;
